import type { TourResult } from "@jpjj/solver-core/types";
import { solveTourExact } from "@jpjj/solver-core/exact";
import { LEVELS } from "../game/levels";
import { openLength, leagues } from "../game/geometry";
import { OPT_EPS } from "../game/scoring";
import { state, matrixFor } from "../game/state";
import { buildMiniChart } from "../game/board";
import { $ } from "../dom";

/* ---------- Per-voyage solves (the maps are fixed, so cache them) ---------- */
const solvePromises: (Promise<TourResult> | null)[] = LEVELS.map(() => null);
const solveResults: (TourResult | null)[] = LEVELS.map(() => null);

function solveLevel(i: number): Promise<TourResult> {
  let pr = solvePromises[i];
  if (!pr) {
    pr = solveTourExact({ points: LEVELS[i].points })
      .then((res) => { solveResults[i] = res; return res; });
    solvePromises[i] = pr;
  }
  return pr;
}

/** Kick off all five solves during play, so the reveal is instant. */
export function prepareSolves(): Promise<TourResult[]> {
  return Promise.all(LEVELS.map((_, i) => solveLevel(i)));
}

/* ---------- Carousel ---------- */
let cmpIndex = 0;
let animCancel = false;
let animRunning = false;
let timers: number[] = [];
const sleep = (ms: number) =>
  new Promise<void>((res) => { const t = window.setTimeout(res, ms); timers.push(t); });
function clearTimers(): void { for (const t of timers) clearTimeout(t); timers = []; }

export function openReveal(): void {
  $("solverOverlay").classList.remove("hidden");
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
  cmpIndex = 0;
  void runLevelCompare(0);
}

async function runLevelCompare(i: number): Promise<void> {
  clearTimers();
  animCancel = false;
  animRunning = true;

  const L = LEVELS[i];
  const rec = state.records[i];
  $("cmpHead").innerHTML = `Voyage ${i + 1} / ${LEVELS.length} — <span class="lv-mon">⛵ ${L.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("optScore").textContent = "—";

  // your side renders immediately from the best recorded round
  buildMiniChart($("youMini"), i, rec.order, true);
  $("youScore").textContent = leagues(rec.length);

  const status = $("solverStatus");
  status.textContent = `Charting Voyage ${i + 1} with Held–Karp…`;
  let res: TourResult;
  try {
    res = await solveLevel(i);
  } catch {
    status.textContent = "The exact solver could not be started — reload and try again.";
    animRunning = false;
    return;
  }
  if (animCancel) return;
  const subsets = (1 << (LEVELS[i].points.length - 1)).toLocaleString("en-US");
  status.textContent = res.status === "OPTIMAL"
    ? `Held–Karp proved the shortest round in ${Math.round(res.tSolveMs)} ms (${subsets} port-subsets)`
    : `Best round found in ${Math.round(res.tSolveMs)} ms (not proven shortest)`;

  await animateOptimal(i, res);
  if (animCancel) return;

  finishLevel(rec.length, res);
}

/** Draw the solver's round leg by leg, the length counting up. */
async function animateOptimal(levelIdx: number, res: TourResult): Promise<void> {
  const ord = res.order.length ? res.order.slice(0, -1) : []; // drop the trailing depot
  const d = matrixFor(levelIdx);
  for (let k = 2; k <= ord.length; k++) {
    if (animCancel) return;
    buildMiniChart($("optMini"), levelIdx, ord.slice(0, k), false);
    $("optScore").textContent = leagues(openLength(ord.slice(0, k), d));
    await sleep(420);
  }
  if (animCancel) return;
  buildMiniChart($("optMini"), levelIdx, ord, true); // the homeward leg
  $("optScore").textContent = leagues(res.length);
}

function finishLevel(youLen: number, res: TourResult): void {
  animRunning = false;
  const proven = res.status === "OPTIMAL";
  const gap = youLen - res.length;
  const gapEl = $("cmpGap");
  if (gap <= OPT_EPS) {
    gapEl.textContent = proven
      ? "You matched the proven shortest round — perfectly charted."
      : "You matched (or beat) the best round the solver found in its time limit.";
  } else {
    const pct = ((gap / res.length) * 100).toFixed(1);
    gapEl.textContent =
      `Your round: ${leagues(youLen)} lg · ${proven ? "proven shortest" : "solver's best"}: ` +
      `${leagues(res.length)} lg — you sailed ${leagues(gap)} lg (+${pct}%) further than needed.`;
  }
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === LEVELS.length - 1 ? "See your run ▸" : "Next voyage ▸";
}

export function skipCompare(): void {
  if (!animRunning) return;
  animCancel = true;
  clearTimers();
  const res = solveResults[cmpIndex];
  const rec = state.records[cmpIndex];
  if (!res) return;
  buildMiniChart($("optMini"), cmpIndex, res.order.slice(0, -1), true);
  $("optScore").textContent = leagues(res.length);
  finishLevel(rec.length, res);
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < LEVELS.length - 1) {
    cmpIndex++;
    void runLevelCompare(cmpIndex);
  } else {
    showFinal();
  }
}

function showFinal(): void {
  const youTotal = state.records.reduce((s, r) => s + r.length, 0);
  const optTotal = solveResults.reduce((s, r) => s + (r ? r.length : 0), 0);
  const allProven = solveResults.every((r) => r && r.status === "OPTIMAL");
  $("fcYou").textContent = leagues(youTotal);
  $("fcOpt").textContent = leagues(optTotal);
  $("fcSub").textContent = allProven ? "leagues · proven shortest" : "leagues · best found";
  const gap = youTotal - optTotal;
  $("fcGap").textContent = gap <= OPT_EPS * LEVELS.length
    ? "A flawless run — across all five voyages you sailed the shortest possible rounds."
    : `Across five voyages you sailed ${leagues(youTotal)} leagues. The optimizer needs only ` +
      `${leagues(optTotal)} — ${leagues(gap)} leagues (+${((gap / optTotal) * 100).toFixed(1)}%) less sailing, same ports.`;

  const rows = LEVELS.map((L, i) => {
    const o = solveResults[i];
    const ov = o ? o.length : 0;
    const yv = state.records[i].length;
    const pct = ov > 0 ? ((yv - ov) / ov) * 100 : 0;
    return `<tr><td>⛵ ${L.name}</td><td>${leagues(yv)}</td><td class="gold">${leagues(ov)}</td>` +
      `<td>${pct <= 0.01 ? "—" : "+" + pct.toFixed(1) + "%"}</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Voyage</th><th>You</th><th>Shortest</th><th>Gap</th></tr></thead><tbody>${rows}</tbody>`;

  $("stageView").style.display = "none";
  $("finalView").style.display = "";
}

export function resetReveal(): void {
  animCancel = true;
  animRunning = false;
  clearTimers();
  cmpIndex = 0;
  $("youMini").innerHTML = "";
  $("optMini").innerHTML = "";
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
}
